import { Practice } from '../../PracticeRegistration/Model/Practice';
import { PracticeLocationData } from '../../PracticeRegistration/Model/PracticeLocationData';
import { YearOption } from './types';
import { LocationType } from '../../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { ServiceFocus } from '../../../../../slices/service-focuses/models/service-focus';
import { ServiceFocusType } from '../../../../../slices/service-focuses/models/enums/service-focus-type.enum';
import { MapShape } from '../../../../../slices/service-focuses/models/enums/map-shape.enum';

export const getYearsRange = (
    smaller = 1980,
    bigger = new Date().getFullYear()
): YearOption[] => {
    const years: YearOption[] = [];
    for (let i = bigger; i >= smaller; i--) {
        years.push({ id: bigger - i, year: i });
    }
    return years;
};

export const createEmptyLocation = (
    mainData: Practice
): PracticeLocationData => ({
    practiceLocationName: mainData.name || '',
    phone: mainData.principalOwnerPhone || '',
    website: mainData.website || '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    fteDvm: 0,
    id: 0,
    practiceFocus: mainData.practiceFocus || 4,
    otherPracticeType: mainData.otherPracticeType || '',
    locationType: LocationType.Urban,
});

export const syncMainToEmptyLocationFields = (
    form: any,
    data: Practice,
    fieldKey: string,
    value: string
) => {
    try {
        const count = Number(data?.numberOfLocations || 0);
        if (!form || !count) return;

        for (let i = 0; i < count; i++) {
            const loc = data?.practiceLocationData?.[i];
            if (!(loc as any)?.[fieldKey]) {
                form.setFieldValue &&
                    form.setFieldValue(
                        `practiceLocationData[${i}].${fieldKey}`,
                        value,
                        false
                    );
            }
        }
    } catch (error) {
        console.error('Error syncing location fields:', error);
    }
};

export const getServiceFocusById = (
    focuses: ServiceFocus[],
    id: number
): ServiceFocus => {
    const focus = focuses.find((f) => f.id === id);
    return focus;
};

export const moveRootOtherFocusToEnd = (focuses: ServiceFocus[]) => {
    const result = [...focuses];

    const index = result.findIndex(
        (f) =>
            f.serviceFocusType === ServiceFocusType.Other &&
            f.isSubFocus === false
    );

    if (index === -1) return result;

    const [rootOtherFocus] = result.splice(index, 1);
    result.push(rootOtherFocus);

    return result;
};

export const isOtherFocus = (
    focuses: ServiceFocus[],
    focusId: number | string
): boolean => {
    const id = Number(focusId);
    const focus = focuses.find((f) => f.id === id);
    if (!focus) return false;

    return (
        focus.serviceFocusType === ServiceFocusType.Other &&
        focus.isSubFocus === false
    );
};

export const getOtherFocus = (
    focuses: ServiceFocus[],
): ServiceFocus => {
    const otherFocus = focuses.find(
        (f) =>
            f.serviceFocusType === ServiceFocusType.Other &&
            f.isSubFocus === false
    );
    return otherFocus;
};

export const mapServiceFocusesToOptions = (
    focuses: ServiceFocus[]
): { [key: number]: string } => {
    return focuses.reduce((options, focus) => {
        options[focus.id] = focus.name;
        return options;
    }, {} as { [key: number]: string });
};

export const mapServiceFocusesToOptionsShort = (
    focuses: ServiceFocus[]
): { [key: number]: string } => {
    return focuses.reduce((options, focus) => {
        options[focus.id] = focus.abbreviation;
        return options;
    }, {} as { [key: number]: string });
};

export const getServiceFocusesForMapLegend = (focuses: ServiceFocus[]): ServiceFocus[] => {
    if (!Array.isArray(focuses)) return [];
    return focuses.filter((f) => !f.isSubFocus);
};

export const mapShapeToSymbol = (shape: MapShape): string => {
    switch (shape) {
        case MapShape.RoundDot:
            return '●';
        case MapShape.Pentagram:
            return '⬟';
        case MapShape.Octagon:
            return '⯃';
        case MapShape.Diamond:
            return '◆';
        case MapShape.Square:
            return '■';
        case MapShape.Triangle:
            return '▲';
        case MapShape.SixPointStar:
            return '✶';
        case MapShape.FivePointStar:
            return '★';
        case MapShape.FourPointStar:
            return '🟆';
        default:
            return '';
    }
};
